import { useState } from 'react';
import { ChevronRight, AlertTriangle, CheckCircle, BookOpen, Shield, ExternalLink, Star, Zap, Users } from 'lucide-react';
import Footer from './Footer';
import { SEO } from './SEO';
import LocalizedLink from './LocalizedLink';
import { useTranslation } from 'react-i18next';

// ─────────────────────────────────────────────────────────────────────────────
// Real hallucination examples — all links are unedited public share threads.
// ChatGPT examples link out. Gemini example shows the fabricated text inline
// (the full admission exchange is at https://gemini.google.com/share/560bbb817cf3).
// ─────────────────────────────────────────────────────────────────────────────
type ExampleItem = {
  id: number;
  question: string;
  aiLabel: string;
  aiError: string;
  proofUrl?: string;
  proofLabel?: string;
  inlineProof?: string;
  inlineProofNote?: string;
};

export default function HallucinationLandingPage() {
  const { t } = useTranslation('main');
  const [activeTab, setActiveTab] = useState<'chatgpt' | 'gemini'>('chatgpt');
  const [activeDemoIdx, setActiveDemoIdx] = useState(0);
  const examples: { chatgpt: ExampleItem[]; gemini: ExampleItem[] } = {
    chatgpt: [
      {
        id: 1,
        question: t('main.proof.examples.chatgpt.1.question'),
        aiLabel: 'ChatGPT',
        aiError: t('main.proof.examples.chatgpt.1.aiError'),
        proofUrl: 'https://chatgpt.com/share/69ae5d45-3924-8008-aebd-86aa9af30d05',
        proofLabel: t('main.proof.examples.chatgpt.1.proofLabel'),
      },
      {
        id: 2,
        question: t('main.proof.examples.chatgpt.2.question'),
        aiLabel: 'ChatGPT',
        aiError: t('main.proof.examples.chatgpt.2.aiError'),
        proofUrl: 'https://chatgpt.com/share/68c9369f-f944-8008-babb-7b051b0c1600',
        proofLabel: t('main.proof.examples.chatgpt.2.proofLabel'),
      },
    ],
    gemini: [
      {
        id: 3,
        question: t('main.proof.examples.gemini.1.question'),
        aiLabel: t('main.proof.examples.gemini.1.aiLabel'),
        aiError: t('main.proof.examples.gemini.1.aiError'),
        inlineProof: t('main.proof.examples.gemini.1.inlineProof'),
        inlineProofNote: t('main.proof.examples.gemini.1.inlineProofNote'),
      },
    ],
  };

  const demoQuestions = [
    {
      label: t('main.proof.demo.q1.label'),
      q: t('main.proof.demo.q1.q'),
      excerpt: t('main.proof.demo.q1.excerpt'),
      source: t('main.proof.demo.q1.source'),
    },
    {
      label: t('main.proof.demo.q2.label'),
      q: t('main.proof.demo.q2.q'),
      excerpt: t('main.proof.demo.q2.excerpt'),
      source: t('main.proof.demo.q2.source'),
    },
    {
      label: t('main.proof.demo.q3.label'),
      q: t('main.proof.demo.q3.q'),
      excerpt: t('main.proof.demo.q3.excerpt'),
      source: t('main.proof.demo.q3.source'),
    },
  ];

  const activeExamples = examples[activeTab];

  return (
    <>
      <SEO
        title={t('main.proof.seo_title')}
        description={t('main.proof.seo_description')}
        url="/proof"
      />

      <div className="min-h-screen bg-white text-gray-900">

        {/* ── 1. HERO ──────────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-b from-blue-950 to-blue-900 text-white">
          <div className="container mx-auto px-6 py-20 text-center max-w-4xl">

            <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 rounded-full px-4 py-2 mb-8 text-sm font-medium">
              <Star className="w-4 h-4 text-amber-400 flex-shrink-0" />
              {t('main.proof.hero.badge')}
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5 leading-tight">
              {t('main.proof.hero.title_line1')}
              <br />{t('main.proof.hero.title_line2')}
              <br />
              <span className="text-amber-400">{t('main.proof.hero.title_highlight')}</span>
            </h1>

            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto leading-relaxed">
              {t('main.proof.hero.description')}
            </p>
            <p className="text-blue-300 text-base mb-6 max-w-xl mx-auto">
              {t('main.proof.hero.subtext')}
            </p>
            {/* Years you won't waste — hero hook */}
            <div className="inline-block bg-white/10 border border-white/20 rounded-xl px-6 py-4 mb-10 max-w-xl mx-auto">
              <p className="text-white text-base font-medium leading-relaxed">
                {t('main.proof.hero.hook_prefix')}
                <span className="text-amber-400"> {t('main.proof.hero.hook_highlight')}</span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LocalizedLink
                to="/sign-up"
                className="bg-amber-400 text-gray-900 px-8 py-3.5 rounded-lg font-bold hover:bg-amber-300 transition flex items-center justify-center text-base"
              >
                {t('main.proof.hero.cta_primary')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </LocalizedLink>
              <a
                href="#proof"
                className="border border-white/30 text-white px-8 py-3.5 rounded-lg font-medium hover:bg-white/10 transition text-center text-base"
              >
                {t('main.proof.hero.cta_secondary')}
              </a>
            </div>

            <p className="text-blue-400 text-xs mt-6">{t('main.proof.hero.no_cc_note')}</p>
          </div>
        </div>

        {/* ── 2. THE PROBLEM ───────────────────────────────────────────────── */}
        <div className="bg-white py-20 border-b border-gray-100">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                {t('main.proof.problem.title_line1')}
                <br />{t('main.proof.problem.title_line2')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                {t('main.proof.problem.description')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: <AlertTriangle className="w-6 h-6 text-red-500" />,
                  bg: 'bg-red-50',
                  border: 'border-red-100',
                  title: t('main.proof.problem.cards.invented.title'),
                  body: t('main.proof.problem.cards.invented.body'),
                },
                {
                  icon: <AlertTriangle className="w-6 h-6 text-orange-500" />,
                  bg: 'bg-orange-50',
                  border: 'border-orange-100',
                  title: t('main.proof.problem.cards.characters.title'),
                  body: t('main.proof.problem.cards.characters.body'),
                },
                {
                  icon: <AlertTriangle className="w-6 h-6 text-yellow-600" />,
                  bg: 'bg-yellow-50',
                  border: 'border-yellow-100',
                  title: t('main.proof.problem.cards.distorted.title'),
                  body: t('main.proof.problem.cards.distorted.body'),
                },
              ].map((card, i) => (
                <div key={i} className={`p-6 rounded-xl border ${card.border} ${card.bg}`}>
                  <div className="mb-3">{card.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>

            <div className="bg-gray-900 rounded-2xl px-8 py-6 text-center">
              <p className="text-white text-lg font-medium leading-relaxed">
                {t('main.proof.problem.callout_prefix')}
                <span className="text-amber-400"> {t('main.proof.problem.callout_highlight')}</span>
              </p>
            </div>
          </div>
        </div>

        {/* ── 2b. THE COST OF WRONG KNOWLEDGE ─────────────────────────────── */}
        <div className="bg-gray-900 py-16">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <p className="text-gray-400 text-sm uppercase tracking-widest font-semibold mb-5">
              {t('main.proof.cost.kicker')}
            </p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
              {t('main.proof.cost.title_line1')}
              <br />
              <span className="text-amber-400">{t('main.proof.cost.title_line2_highlight')}</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              {t('main.proof.cost.body')}
            </p>
            <p className="text-white text-lg font-medium">
              {t('main.proof.cost.closing_prefix')}
              <span className="text-amber-400"> {t('main.proof.cost.closing_highlight')}</span>
            </p>
          </div>
        </div>

        {/* ── 3. SIDE-BY-SIDE PROOF ────────────────────────────────────────── */}
        <div id="proof" className="bg-gray-50 py-20">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('main.proof.proof.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                {t('main.proof.proof.subtitle')}
              </p>

              <div className="flex justify-center gap-2">
                {(['chatgpt', 'gemini'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition border ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                    }`}
                  >
                    {tab === 'chatgpt' ? t('main.proof.proof.tabs.chatgpt') : t('main.proof.proof.tabs.gemini')}
                  </button>
                ))}
              </div>
            </div>

            {activeExamples.map((example) => (
              <div key={example.id} className="mb-10 rounded-2xl overflow-hidden shadow-lg border border-gray-200">

                {/* Shared question */}
                <div className="bg-gray-800 px-6 py-4 flex items-start gap-3">
                  <span className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-0.5 flex-shrink-0 pt-0.5">
                    {t('main.proof.proof.card.question_label')}
                  </span>
                  <p className="text-white text-sm leading-relaxed italic">
                    "{example.question}"
                  </p>
                </div>

                <div className="grid md:grid-cols-2">

                  {/* Left — fabricated */}
                  <div className="border-r border-gray-200">
                    <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
                      <span className="text-white font-semibold text-sm">
                        {example.aiLabel} — {t('main.proof.proof.card.left.title_suffix')}
                      </span>
                    </div>
                    <div className="bg-white p-5">
                      <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4 min-h-[120px]">
                        <div className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 rounded-full px-2.5 py-1 text-xs font-semibold mb-3">
                          <AlertTriangle className="w-3 h-3" />
                          {t('main.proof.proof.card.left.badge')}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed italic">
                          {example.aiError}
                        </p>
                      </div>
                      {'inlineProof' in example && example.inlineProof ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
                            {t('main.proof.proof.card.left.inline_quote_label', { aiLabel: example.aiLabel })}
                          </p>
                          <p className="text-sm text-gray-800 italic leading-relaxed border-l-4 border-amber-400 pl-3 mb-3 whitespace-pre-line">
                            {example.inlineProof}
                          </p>
                          <p className="text-xs text-amber-700 leading-relaxed">
                            {example.inlineProofNote}
                          </p>
                        </div>
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
                            {t('main.proof.proof.card.left.primary_source_label')}
                          </p>
                          <a
                            href={example.proofUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2"
                          >
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                            {example.proofLabel}
                          </a>
                          <p className="text-xs text-amber-700 mt-2 leading-relaxed">
                            {t('main.proof.proof.card.left.open_note', { aiLabel: example.aiLabel })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right — Sarvalok */}
                  <div>
                    <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-white font-semibold text-sm">
                        {t('main.proof.proof.card.right.title')}
                      </span>
                    </div>
                    <div className="bg-white p-5">
                      <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-4 min-h-[120px]">
                        <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 rounded-full px-2.5 py-1 text-xs font-semibold mb-3">
                          <CheckCircle className="w-3 h-3" />
                          {t('main.proof.proof.card.right.badge')}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {t('main.proof.proof.card.right.description')}
                        </p>
                      </div>
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">
                          {t('main.proof.proof.card.right.list_title')}
                        </p>
                        <ul className="text-xs text-blue-900 space-y-1.5 mt-1">
                          {[
                            t('main.proof.proof.card.right.list_item1'),
                            t('main.proof.proof.card.right.list_item2'),
                            t('main.proof.proof.card.right.list_item3'),
                            t('main.proof.proof.card.right.list_item4'),
                          ].map((item) => (
                            <li key={item} className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 3b. THE KNOWLEDGE GAP ────────────────────────────────────────── */}
        <div className="bg-blue-950 py-16">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <p className="text-blue-400 text-sm uppercase tracking-widest font-semibold mb-5">
              {t('main.proof.gap.kicker')}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
              {t('main.proof.gap.title_line1')}
              <br />
              {t('main.proof.gap.title_line2_prefix')}{' '}
              <span className="text-amber-400">{t('main.proof.gap.title_line2_highlight')}</span>{' '}
              {t('main.proof.gap.title_line2_suffix')}
            </h2>
            <p className="text-gray-300 text-base leading-relaxed max-w-2xl mx-auto mb-6">
              {t('main.proof.gap.p1')}
            </p>
            <p className="text-gray-300 text-base leading-relaxed max-w-2xl mx-auto mb-5">
              {t('main.proof.gap.p2')}
            </p>
            <p className="text-white text-base leading-relaxed max-w-2xl mx-auto mb-8">
              {t('main.proof.gap.p3_prefix')}{' '}
              <span className="text-amber-400">{t('main.proof.gap.p3_highlight')}</span>{' '}
              {t('main.proof.gap.p3_suffix')}
            </p>
            <div className="inline-block bg-white/10 border border-white/20 rounded-xl px-6 py-3">
              <p className="text-white text-sm font-medium">
                {t('main.proof.gap.callout')}
              </p>
            </div>
          </div>
        </div>

        {/* ── 4. WHAT MAKES SARVALOK DIFFERENT ─────────────────────────────── */}
        <div className="bg-white py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('main.proof.pillars.title')}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <BookOpen className="w-6 h-6 text-blue-600" />,
                  bg: 'bg-blue-50',
                  title: t('main.proof.pillars.cards.sources.title'),
                  body: t('main.proof.pillars.cards.sources.body'),
                },
                {
                  icon: <CheckCircle className="w-6 h-6 text-green-600" />,
                  bg: 'bg-green-50',
                  title: t('main.proof.pillars.cards.noHallucinations.title'),
                  body: t('main.proof.pillars.cards.noHallucinations.body'),
                },
                {
                  icon: <Zap className="w-6 h-6 text-purple-600" />,
                  bg: 'bg-purple-50',
                  title: t('main.proof.pillars.cards.depth.title'),
                  body: t('main.proof.pillars.cards.depth.body'),
                },
              ].map((card, i) => (
                <div key={i} className="p-6 rounded-xl bg-white shadow-lg hover:shadow-xl transition border border-gray-100">
                  <div className={`w-12 h-12 ${card.bg} rounded-lg flex items-center justify-center mb-4`}>
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">{card.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 5. PRODUCT DEMO ──────────────────────────────────────────────── */}
        <div className="bg-gray-50 py-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('main.proof.demo.title')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('main.proof.demo.subtitle_prefix')}
                <span className="font-medium text-gray-800"> {t('main.proof.demo.subtitle_highlight')}</span>
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {demoQuestions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveDemoIdx(i)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition border ${
                    activeDemoIdx === i
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                  }`}
                >
                  {demoQuestions[i].label}
                </button>
              ))}
            </div>

            {/* Chat UI */}
            <div className="border border-blue-100 rounded-2xl shadow-xl overflow-hidden bg-white">
              <div className="px-5 py-4 border-b border-blue-900 bg-blue-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded bg-white/15 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">S</span>
                  </div>
                  <span className="text-white font-medium">Sarvalok AI</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-white/20" />
                  <div className="w-3 h-3 rounded-full bg-white/20" />
                  <div className="w-3 h-3 rounded-full bg-white/20" />
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium bg-blue-50 text-blue-700">
                    {t('main.proof.demo.you_label')}
                  </div>
                  <div className="flex-1 rounded-lg p-4 bg-blue-50 text-gray-900 text-sm leading-relaxed">
                    {demoQuestions[activeDemoIdx].q}
                  </div>
                </div>

                <div className="h-px bg-blue-100" />

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm bg-blue-600">
                    S
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 text-sm leading-relaxed mb-3">
                      {demoQuestions[activeDemoIdx].excerpt}
                    </p>
                    <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-800 font-medium">
                      <BookOpen className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      {t('main.proof.demo.source_label')}: {demoQuestions[activeDemoIdx].source}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 py-3 border-t border-blue-100 bg-blue-50">
                <p className="text-xs text-blue-700/70 text-center">
                  {t('main.proof.demo.sample_note')}
                </p>
              </div>
            </div>

            <div className="text-center mt-8">
              <LocalizedLink
                to="/sign-up"
                className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                {t('main.proof.demo.cta')}
                <ChevronRight className="ml-2 w-4 h-4" />
              </LocalizedLink>
            </div>
          </div>
        </div>

        {/* ── 6. INTELLECTUAL AUTHORITY ────────────────────────────────────── */}
        <div className="bg-white py-20 border-t border-gray-100">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('main.proof.authority.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-xl mx-auto">
                {t('main.proof.authority.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  quote: t('main.proof.authority.quotes.1.quote'),
                  name: t('main.proof.authority.quotes.1.name'),
                  role: t('main.proof.authority.quotes.1.role'),
                },
                {
                  quote: t('main.proof.authority.quotes.2.quote'),
                  name: t('main.proof.authority.quotes.2.name'),
                  role: t('main.proof.authority.quotes.2.role'),
                },
                {
                  quote: t('main.proof.authority.quotes.3.quote'),
                  name: t('main.proof.authority.quotes.3.name'),
                  role: t('main.proof.authority.quotes.3.role'),
                },
                {
                  quote: t('main.proof.authority.quotes.4.quote'),
                  name: t('main.proof.authority.quotes.4.name'),
                  role: t('main.proof.authority.quotes.4.role'),
                },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <p className="text-gray-700 text-sm leading-relaxed italic mb-4">{item.quote}</p>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                    <p className="text-gray-500 text-xs">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-900 rounded-2xl p-8 text-white text-center">
              <div
                className="text-2xl leading-10 mb-3 text-blue-200 italic"
                style={{ fontFamily: "'Tiro Devanagari Sanskrit', serif" }}
              >
                न हि ज्ञानेन सदृशं पवित्रमिह विद्यते।
              </div>
              <p className="text-blue-100 italic text-sm mb-1">
                {t('main.proof.authority.sanskrit_translation')}
              </p>
              <p className="text-blue-400 text-xs font-medium">— Bhagavad Gītā 4.38</p>
            </div>
          </div>
        </div>

        {/* ── 7. WHO IT'S FOR ──────────────────────────────────────────────── */}
        <div className="bg-gray-50 py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('main.proof.for.title')}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {[
                {
                  icon: <Zap className="w-5 h-5 text-purple-600" />,
                  label: t('main.proof.for.cards.yoga.label'),
                  sub: t('main.proof.for.cards.yoga.sub'),
                },
                {
                  icon: <Users className="w-5 h-5 text-blue-600" />,
                  label: t('main.proof.for.cards.diaspora.label'),
                  sub: t('main.proof.for.cards.diaspora.sub'),
                },
                {
                  icon: <BookOpen className="w-5 h-5 text-green-600" />,
                  label: t('main.proof.for.cards.scholars.label'),
                  sub: t('main.proof.for.cards.scholars.sub'),
                },
                {
                  icon: <Star className="w-5 h-5 text-amber-500" />,
                  label: t('main.proof.for.cards.astrology.label'),
                  sub: t('main.proof.for.cards.astrology.sub'),
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm transition"
                >
                  <div className="mb-3">{item.icon}</div>
                  <p className="font-semibold text-gray-900 text-sm mb-2">{item.label}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.sub}</p>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* ── 8. PRICING ───────────────────────────────────────────────────── */}
        <div className="bg-white py-20 border-t border-gray-100">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {t('main.proof.pricing.title')}
            </h2>
            <p className="text-gray-500 text-base mb-10">
              {t('main.proof.pricing.subtitle')}
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {/* Global */}
              <div className="bg-blue-900 text-white rounded-2xl p-8 shadow-xl">
                <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-3">{t('main.proof.pricing.global.label')}</p>
                <p className="text-5xl font-extrabold mb-1">$19.99</p>
                <p className="text-blue-300 text-sm mb-6">{t('main.proof.pricing.per_month')}</p>
                <ul className="text-sm text-blue-100 space-y-2 text-left mb-8">
                  {[
                    t('main.proof.pricing.features.1'),
                    t('main.proof.pricing.features.2'),
                    t('main.proof.pricing.features.3'),
                    t('main.proof.pricing.features.4'),
                    t('main.proof.pricing.features.5'),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <LocalizedLink
                  to="/sign-up"
                  className="block w-full bg-amber-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-amber-300 transition text-center"
                >
                  {t('main.proof.pricing.global.cta')}
                </LocalizedLink>
              </div>

              {/* India */}
              <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-8">
                <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">{t('main.proof.pricing.india.label')}</p>
                <p className="text-5xl font-extrabold text-gray-900 mb-1">₹1,179</p>
                <p className="text-gray-400 text-sm mb-6">{t('main.proof.pricing.per_month')}</p>
                <ul className="text-sm text-gray-600 space-y-2 text-left mb-8">
                  {[
                    t('main.proof.pricing.india_features.1'),
                    t('main.proof.pricing.india_features.2'),
                    t('main.proof.pricing.india_features.3'),
                    t('main.proof.pricing.india_features.4'),
                    t('main.proof.pricing.india_features.5'),
                    t('main.proof.pricing.india_features.6'),
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <LocalizedLink
                  to="/sign-up"
                  className="block w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition text-center"
                >
                  {t('main.proof.pricing.india.cta')}
                </LocalizedLink>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              {t('main.proof.pricing.note')}
            </p>
            <LocalizedLink
              to="/pricing"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium underline underline-offset-2"
            >
              {t('main.proof.pricing.link')}
            </LocalizedLink>

            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4">
              <p className="text-amber-800 text-sm font-medium">
                {t('main.proof.pricing.notice')}
              </p>
              <p className="text-amber-700 text-xs mt-2 leading-relaxed">
                {t('main.proof.pricing.notice_asterisk', '').split('<terms>').map((part: string, i: number) => {
                  if (i === 0) return part;
                  const [linkText, rest] = part.split('</terms>');
                  return (
                    <span key={i}>
                      <LocalizedLink to="/terms" className="underline hover:text-amber-900">{linkText}</LocalizedLink>
                      {rest}
                    </span>
                  );
                })}
              </p>
            </div>

          </div>
        </div>

        {/* ── 9. FINAL CLOSE ───────────────────────────────────────────────── */}
        <div className="bg-gradient-to-b from-blue-900 to-blue-950 py-24">
          <div className="container mx-auto px-6 max-w-3xl text-center">

            <div className="inline-block bg-white/10 border border-white/20 rounded-xl px-6 py-4 mb-8 max-w-xl mx-auto">
              <p className="text-white text-base font-medium leading-relaxed">
                {t('main.proof.close.hook_prefix')}
                <span className="text-amber-400"> {t('main.proof.close.hook_highlight')}</span>
              </p>
            </div>

            <p className="text-blue-300 text-base italic mb-6 leading-relaxed max-w-xl mx-auto">
              {t('main.proof.close.p1')}
              <br />
              <span className="text-white font-medium not-italic">{t('main.proof.close.p2')}</span>
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
              {t('main.proof.close.title_line1')}
              <br />
              <span className="text-amber-400">{t('main.proof.close.title_line2_highlight')}</span>
            </h2>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LocalizedLink
                to="/sign-up"
                className="bg-amber-400 text-gray-900 px-10 py-4 rounded-lg font-bold text-lg hover:bg-amber-300 transition flex items-center justify-center"
              >
                {t('main.proof.close.cta_primary')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </LocalizedLink>
              <LocalizedLink
                to="/pricing"
                className="border border-white/30 text-white px-10 py-4 rounded-lg font-medium hover:bg-white/10 transition text-center"
              >
                {t('main.proof.close.cta_secondary')}
              </LocalizedLink>
            </div>

            <p className="text-blue-400 text-xs mt-8">
              {t('main.proof.close.footer_note')}
            </p>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
